import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '../lib/logger.js';
import { Boton } from './ui.js';

/**
 * Frontera de error de React.
 *
 * Motivo: una excepcion durante el render dejaba la aplicacion **en blanco**,
 * sin un solo mensaje. En el navegador se ve la causa en la consola; en el APK
 * de Android no hay consola que mirar, asi que la pantalla en blanco era todo
 * lo que quedaba. `useLiveQuery` relanza en el render el error de su consulta,
 * de modo que cualquier fallo leyendo la base local llegaba aqui.
 *
 * Lo que hace esta frontera: registrar el fallo en el anillo del `logger`
 * —visible en Mas -> Sincronizacion y exportable— y enseñar el texto del error
 * en pantalla. Es la misma regla que el CLAUDE.md aplica al trabajo asincrono
 * de arranque: un fallo silencioso es peor que uno ruidoso.
 */

interface Props {
  children: ReactNode;
  /** Que parte de la aplicacion protege. Aparece en el registro y en pantalla. */
  ambito: string;
}

interface State {
  error: Error | null;
  pila: string | null;
}

function detalle(error: Error, pila: string | null): string {
  const cabecera = `${error.name}: ${error.message}`;
  const trazas = [error.stack ?? '', pila ?? ''].filter((texto) => texto !== '').join('\n');
  return trazas === '' ? cabecera : `${cabecera}\n${trazas}`;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, pila: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      error: error instanceof Error ? error : new Error(String(error)),
      pila: null,
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ pila: info.componentStack ?? null });
    logger.error('ui', 'RENDER_CRASH', 'Una pantalla ha fallado al dibujarse', {
      ambito: this.props.ambito,
      // El mensaje puede citar un campo de una fila del usuario; el `logger`
      // ya redacta las claves sensibles antes de escribir nada.
      error: `${error.name}: ${error.message}`,
      stack: (error.stack ?? '').split('\n').slice(0, 6).join('\n'),
      componentStack: (info.componentStack ?? '').split('\n').slice(0, 6).join('\n'),
    });
  }

  private readonly reintentar = (): void => {
    this.setState({ error: null, pila: null });
  };

  private readonly copiar = (): void => {
    const { error, pila } = this.state;
    if (error === null) return;
    void navigator.clipboard?.writeText(detalle(error, pila)).catch(() => {
      // Sin portapapeles el texto sigue visible en pantalla: no hay nada que
      // recuperar y avisar de esto no ayudaria a nadie.
    });
  };

  override render(): ReactNode {
    const { error, pila } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="fallo" role="alert">
        <p className="estado__titulo">Esta pantalla ha fallado</p>
        <p className="estado__descripcion">
          El resto de la aplicación sigue funcionando y no se ha perdido nada de lo guardado.
          Este es el error tal cual, para poder arreglarlo:
        </p>
        <pre className="fallo__detalle">{detalle(error, pila)}</pre>
        <div className="fallo__acciones">
          <Boton onClick={this.reintentar}>Reintentar</Boton>
          <Boton variante="secundario" onClick={this.copiar}>
            Copiar detalle
          </Boton>
        </div>
        <p className="campo__pista">Ámbito: {this.props.ambito}</p>
      </div>
    );
  }
}
